import { DBFactory } from "~/classes/database/db_factory";
import { AssignmentType } from "~/types/enums/AssignmentType.enum";
import { BaseModel } from "../base.model";
import { Classroom } from "../classrooms/classroom.model";
import {
  EducationResource,
  EducationResources,
} from "../education-resources/education-resource.model";

export type Assignments = Assignment[];

export type StudentChecklistOverride = {
  studentIds: string[];
  checklistId: string;
  checklistPath: string;
};

type AssignmentConstrutorParams = {
  id?: string;
  externalId: string;
  externalProvider: "sakai" | "google" | "canvas" | "iew";
  externalProviderUserId: string;
  organizationId?: string;
  userId: string;
  name: string;
  type?: AssignmentType;
  _type?: AssignmentType;
  classroomId: string;
  classroomName: string;
  status: "OPEN" | "GRADED" | "CLOSED";
  openTimestamp?: number;
  dueTimestamp?: number | undefined;
  closeTimestamp?: number;
  maxPoints?: number | undefined;
  checklistId?: string | null;
  checklistPath?: string | null;
  enableStyleOverrides: boolean;
  availableStyleCriteria?: string[];
  lastSyncedTimestamp?: number;
  lastUpdatedTimestamp?: number;
  isVisibleToStudents?: boolean;
  isArchived: boolean;
  numGradedDocuments?: number;
  numGradedFinalDraftDocuments?: number;
  allowKWOSubmission?: boolean;
  requireKWOSubmission?: boolean;
  allowRoughDraftSubmission?: boolean;
  requireRoughDraftSubmission?: boolean;
  requireRoughDraftChecklist?: boolean;
  roughDraftSubmissionFormats?: string[];

  allowFinalDraftSubmission?: boolean;
  // If set to true, teacher will be probmpet to mark the final draft as  accept / incomplete instead of showing a grade to the student
  allowFinalDraftGradedState?: boolean;
  displayOrder?: number;
  publicNotes?: any;
  studentChecklistOverrides?: StudentChecklistOverride[];
  resources?: EducationResources;
  excusedStudentIds: string[];
  isAutoReturnEnabled?: boolean;

  levelId?: string;
  lessonId?: string;
};

export class Assignment extends BaseModel {
  id?: string = "";
  externalId: string;
  organizationId?: string;
  externalProvider: "sakai" | "google" | "canvas" | "iew" = "iew";
  externalProviderUserId: string = "";
  userId: string = "";
  name: string = "";
  classroomId: string = "";
  classroomName: string = "";
  status: "OPEN" | "GRADED" | "CLOSED" = "OPEN";
  openTimestamp?: number = new Date().getTime();
  lastUpdatedTimestamp?: number;
  dueTimestamp?: number;
  closeTimestamp?: number;
  maxPoints?: number = 0;
  checklistId?: string | null = "";
  checklistPath?: string | null = "";
  enableStyleOverrides: boolean = false;
  availableStyleCriteria?: string[];
  lastSyncedTimestamp?: number;
  isArchived: boolean = false;
  isVisibleToStudents: boolean = true;
  numGradedDocuments?: number;
  numGradedFinalDraftDocuments?: number;
  requireKWOSubmission?: boolean;
  allowKWOSubmission?: boolean;
  requireRoughDraftSubmission?: boolean;
  allowRoughDraftSubmission?: boolean;
  requireRoughDraftChecklist?: boolean;
  roughDraftSubmissionFormats?: string[];
  allowFinalDraftGradedState?: boolean;
  allowFinalDraftSubmission: boolean;
  displayOrder?: number;
  // private _type: AssignmentType;
  _type: AssignmentType;
  publicNotes?: any;
  studentChecklistOverrides?: StudentChecklistOverride[];
  resources: EducationResources;
  excusedStudentIds: string[];
  isAutoReturnEnabled?: boolean;

  levelId?: string;
  lessonId?: string;

  constructor({
    id,
    externalId,
    externalProvider,
    externalProviderUserId,
    organizationId,
    userId,
    name,
    classroomId,
    classroomName,
    status,
    openTimestamp,
    lastUpdatedTimestamp,
    dueTimestamp,
    closeTimestamp,
    maxPoints,
    checklistId,
    checklistPath,
    enableStyleOverrides,
    availableStyleCriteria,
    lastSyncedTimestamp,
    isVisibleToStudents,
    isArchived,
    numGradedDocuments,
    numGradedFinalDraftDocuments,
    requireKWOSubmission,
    allowKWOSubmission,
    requireRoughDraftSubmission,
    allowRoughDraftSubmission,
    requireRoughDraftChecklist,
    roughDraftSubmissionFormats,
    allowFinalDraftGradedState,
    allowFinalDraftSubmission,
    displayOrder,
    type,
    _type,
    publicNotes,
    studentChecklistOverrides,
    resources,
    excusedStudentIds,
    isAutoReturnEnabled,
    levelId,
    lessonId,
  }: AssignmentConstrutorParams) {
    super();

    this.id = id;
    this.externalId = externalId;
    this.externalProvider = externalProvider;
    this.externalProviderUserId = externalProviderUserId;
    this.organizationId = organizationId;
    this.userId = userId;
    this.name = name;
    this.classroomId = classroomId;
    this.classroomName = classroomName;
    this.status = status;
    this.openTimestamp = openTimestamp;
    this.lastUpdatedTimestamp = lastUpdatedTimestamp;
    this.dueTimestamp = dueTimestamp;
    this.closeTimestamp = closeTimestamp;
    this.maxPoints = maxPoints;
    this.checklistId = checklistId;
    this.checklistPath = checklistPath;
    this.requireRoughDraftChecklist = requireRoughDraftChecklist;
    this.roughDraftSubmissionFormats = roughDraftSubmissionFormats;
    this.enableStyleOverrides = enableStyleOverrides;
    this.availableStyleCriteria = availableStyleCriteria;
    this.lastSyncedTimestamp = lastSyncedTimestamp ?? 0;
    this.isVisibleToStudents = isVisibleToStudents ?? true;
    this.isArchived = isArchived;
    this.numGradedDocuments = numGradedDocuments;
    this.numGradedFinalDraftDocuments = numGradedFinalDraftDocuments;

    this.allowKWOSubmission = allowKWOSubmission;
    this.requireKWOSubmission =
      requireKWOSubmission ?? allowKWOSubmission ?? undefined;

    this.allowRoughDraftSubmission = allowRoughDraftSubmission;
    this.requireRoughDraftSubmission =
      requireRoughDraftSubmission ?? allowRoughDraftSubmission ?? undefined;

    this.allowFinalDraftGradedState = allowFinalDraftGradedState;
    this.allowFinalDraftSubmission = allowFinalDraftSubmission ?? true;
    this.displayOrder = displayOrder;
    this._type = _type ?? type ?? AssignmentType.iewComposition;
    this.publicNotes = publicNotes;
    this.studentChecklistOverrides = studentChecklistOverrides;
    this.isAutoReturnEnabled = isAutoReturnEnabled;
    this.resources = resources ?? [];
    this.excusedStudentIds = excusedStudentIds ?? [];
    this.levelId = levelId;
    this.lessonId = lessonId;
    this.modelDatabaseConfig = {
      collection: "/assignments",
      path: `/assignments/${this.id}`,
    };
  }

  get type() {
    return this._type;
  }

  set type(type: AssignmentType) {
    this._type = type;

    if (this._type == AssignmentType.general) {
      this.checklistId = undefined;
      this.checklistPath = undefined;
      this.allowKWOSubmission = false;
      this.allowRoughDraftSubmission = false;
    }
  }

  override toMap(): object {
    return super.toMap();
  }

  static default(userId: string, classroomId: string, classroomName: string) {
    return new Assignment({
      name: "",
      userId: userId,
      classroomId,
      classroomName: classroomName,
      externalId: "",
      externalProvider: "iew",
      status: "OPEN",
      externalProviderUserId: userId,
      enableStyleOverrides: false,
      isArchived: false,
      numGradedDocuments: 0,
      numGradedFinalDraftDocuments: 0,
      type: AssignmentType.iewComposition,
      allowFinalDraftSubmission: true,
      resources: [],
      excusedStudentIds: [],

      // Defaults
      isAutoReturnEnabled: true,
      allowKWOSubmission: false,
      allowRoughDraftSubmission: false,

      allowFinalDraftGradedState: false,
    });
  }

  static override fromMap(map: any): Assignment {
    if (map.resources != undefined) {
      map.resources = map.resources.map((resource: any) => {
        return EducationResource.fromMap(resource);
      });
    }

    const instance = new Assignment(map);
    instance.modelDatabaseConfig = {
      collection: "/assignments",
      path: `/assignments/${instance.id}`,
    };

    return instance;
  }

  get isAssignmentActive(): boolean {
    const firstTimestampOfWeek = useFirstTimestampOfWeek();
    const lastTimestampOfWeek = useLastTimestampOfWeek();

    return (
      this.dueTimestamp! >= firstTimestampOfWeek &&
      this.dueTimestamp! <= lastTimestampOfWeek
    );
  }

  allowsSubmissionType({
    classroom,
    submissionType,
  }: {
    classroom?: Classroom;
    submissionType: DocumentSubmissionType;
  }) {
    if (submissionType == DocumentSubmissionType.finalDraft) {
      return true;
    }

    if (this.type == AssignmentType.general) {
      return false;
    }

    if (submissionType == DocumentSubmissionType.keyWordOutline) {
      return this.allowKWOSubmission != undefined
        ? this.allowKWOSubmission
        : classroom?.allowKWOSubmission == true;
    }

    if (submissionType == DocumentSubmissionType.roughDraft) {
      return this.allowRoughDraftSubmission != undefined
        ? this.allowRoughDraftSubmission
        : classroom?.allowRoughDraftSubmission == true;
    }

    return false;
  }

  requiresAssignmentCompontent({
    classroom,
    submissionType,
  }: {
    classroom: Classroom;
    submissionType: DocumentSubmissionType;
  }) {
    if (submissionType == DocumentSubmissionType.finalDraft) {
      return true;
    }

    if (this.type == AssignmentType.general) {
      return false;
    }

    if (submissionType == DocumentSubmissionType.keyWordOutline) {
      return this.allowKWOSubmission != undefined
        ? this.allowKWOSubmission
        : classroom.allowKWOSubmission;
    }

    if (submissionType == DocumentSubmissionType.roughDraft) {
      return this.allowRoughDraftSubmission != undefined
        ? this.allowRoughDraftSubmission
        : classroom.allowRoughDraftSubmission;
    }

    return false;
  }

  isStudentExcused(studentId: string): boolean {
    return this.excusedStudentIds.includes(studentId);
  }

  get acceptRoughDraftInAnyFormat(): boolean {
    return (
      this.requireRoughDraftChecklist == false &&
      this.roughDraftSubmissionFormats?.some((format) =>
        useKWOFileTypes().includes(format)
      ) == true
    );
  }

  async syncSubmissions() {
    await await useFetch(`/api/lms/sync/assignment/${this.id}/submissions`, {
      headers: await useApiHeaders(),
    });
  }

  async save() {
    const db = DBFactory.createDatabase();
    this.lastUpdatedTimestamp = Date.now();
    this.id = await db.save(this.toMap(), this.modelDatabaseConfig);
  }
}
